/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useRouteMatch } from 'react-router-dom';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { companyTrackerUserRoles, portfolioTrackerUserRoles, UserFilterReportingLevel, UserRoles } from '../../constants/user';

type FilterValue = UserRoles | 'active' | 'pending' | undefined;

interface UserFilterProps {
  filterStatus?: FilterValue;
  handleStatusChange: (newValue: any) => void;
  filterPermission?: FilterValue;
  handlePermissionChange: (newValue: any) => void;
  filterReportingLevel?: UserFilterReportingLevel;
  handleReportingLevelChange: (newValue: any) => void;
  defaultOptionText?: string;
}


export const UserFilter = ({
  filterStatus,
  handleStatusChange,
  filterPermission,
  handlePermissionChange,
  filterReportingLevel,
  handleReportingLevelChange,
  defaultOptionText = 'All user statuses',
}: UserFilterProps) => {
  const match = useRouteMatch<{ initiativeId?: string; portfolioId?: string }>();
  const isPortfolioTracker = !!match.params.portfolioId;
  const userRoles = isPortfolioTracker ? portfolioTrackerUserRoles : companyTrackerUserRoles;
  const options: { value: FilterValue; label: string }[] = [
    {
      value: undefined,
      label: defaultOptionText,
    },
    {
      value: 'active',
      label: 'Active Users',
    },
    {
      value: 'pending',
      label: 'Pending Users',
    },
  ];
  const currentValue = options.find((o) => o.value === filterStatus) || null;

  const permissionOptions: { value: FilterValue; label: string }[] = [
    {
      value: undefined,
      label: 'All user permissions',
    },
    ...userRoles.filter(r => r.code !== UserRoles.ContributorVerifier).map((r) => ({
      value: r.code,
      label: r.label,
    })),
  ];
  const currentPermissionValue = permissionOptions.find((o) => o.value === filterPermission) || null;

  const reportingLevelOptions: { value: UserFilterReportingLevel; label: string }[] = [
    {
      value: 'inherited',
      label: 'Exclude subsidiaries',
    },
    {
      value: 'inherited_and_subsidiaries',
      label: 'Include subsidiaries',
    },
  ];
  const currentReportingLevelValue = reportingLevelOptions.find((o) => o.value === filterReportingLevel) || null;

  const selectOptions = [
    {
      placeholder: 'Select user status',
      options,
      value: currentValue,
      onChange: handleStatusChange,
    },
    {
      placeholder: 'Select user permission',
      options: permissionOptions,
      value: currentPermissionValue,
      onChange: handlePermissionChange,
    },
    {
      placeholder: 'Select reporting level',
      options: reportingLevelOptions,
      value: currentReportingLevelValue,
      onChange: handleReportingLevelChange,
    },
  ];

  return (
    <div className='d-flex align-items-center gap-2'>
      {selectOptions.map((props, index) => (
        <SelectFactory
          key={index}
          selectType={SelectTypes.SingleSelect}
          isFlexibleSize
          {...props}
        />
      ))}
    </div>
  );
};
